import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/TimberType';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const TIMBER_TYPE_API_REDUCER_KEY = 'timber-types';
export const TIMBER_TYPE_PATH = 'timber-types';

export const timberTypeApi = createApi({
    reducerPath: TIMBER_TYPE_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['TimberType', 'TimberTypes'],
    endpoints: (builder) => ({
        getTimberTypeList: builder.query<PaginationResult<Entity.TimberType>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ TIMBER_TYPE_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['TimberTypes'],
        }),
        getTimberType: builder.query<Entity.TimberType, Entity.PrimaryTimberType>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = timberTypeNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ TIMBER_TYPE_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['TimberType'],
        }),
        getTimberTypeMultiple: builder.query<Entity.TimberType, Entity.PrimaryTimberType[]>({
            query: (ids) => {
                return ({
                    url: `/${ TIMBER_TYPE_PATH }/ids${Entity.PrimaryTimberType.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['TimberTypes'],
        }),
        getTimberTypePresentation: builder.query<PaginationResult<Entity.TimberType>, Entity.PresentationTimberTypeRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ TIMBER_TYPE_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['TimberTypes'],
        }),
        
        insertTimberType: builder.mutation<Entity.TimberType, Entity.CreateTimberType>({
            query: (toInsert) => {
                return ({
                    url: `/${ TIMBER_TYPE_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    timberTypeApi.util.upsertQueryData('getTimberType', Entity.PrimaryTimberType.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['TimberTypes'],
        }),
        insertMultipleTimberType: builder.mutation<Entity.TimberType[], Entity.CreateTimberType[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ TIMBER_TYPE_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            timberTypeApi.util.upsertQueryData('getTimberType', Entity.PrimaryTimberType.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['TimberTypes'],
        }),
        updateTimberType: builder.mutation<Entity.TimberType, Entity.UpdateEntityTimberType>({
            query: (toUpdate) => {
                return ({
                    url: `/${ TIMBER_TYPE_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['TimberTypes'],
        }),
        updateMultipleTimberType: builder.mutation<Entity.TimberType[], Entity.UpdateEntityTimberType[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ TIMBER_TYPE_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['TimberTypes'],
        }),
        deleteTimberType: builder.mutation<Entity.TimberType, Entity.TimberType>({
            query: (toDelete) => {
                return ({
                    url: `/${ TIMBER_TYPE_PATH }/${Entity.PrimaryTimberType.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['TimberTypes'],
        }),
        deleteMultipleTimberType: builder.mutation<Entity.TimberType[], Entity.PrimaryTimberType[]>({
            query: (ids) => {
                return ({
                    url: `/${ TIMBER_TYPE_PATH }/batch${Entity.PrimaryTimberType.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['TimberTypes'],
        }),
    }),
})

export const [timberTypeNormalizer, timberTypeNormalizerMiddleware] = createNormalizationMiddleware(TIMBER_TYPE_API_REDUCER_KEY, timberTypeApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetTimberTypeListQuery,
    useGetTimberTypeMultipleQuery,
    useGetTimberTypePresentationQuery,
    useGetTimberTypeQuery,
    useInsertTimberTypeMutation,
    useInsertMultipleTimberTypeMutation,
    useUpdateTimberTypeMutation,
    useUpdateMultipleTimberTypeMutation,
    useDeleteTimberTypeMutation,
    useDeleteMultipleTimberTypeMutation,
    
} = timberTypeApi
